'use client'

import { useId, useRef } from 'react'
import { dir } from 'i18next'
import { useTheme } from '@mui/material/styles'
import { useMediaQuery, Grid, Typography } from '@mui/material'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'

// Types
import type { DialogueProps } from '@/types/components/molecules/dialogue'

// Helpers
import { useTranslation } from '@/helpers/i18n/client'

const DialogueMolecule = (props: DialogueProps) => {
	// Props
	const { lng, text } = props

	// Variables
	const lngDir = dir(lng)
	const { t } = useTranslation(lng)
	const muiTheme = useTheme()
	const patterId = useId()
	const gridRef = useRef<HTMLDivElement>(null)
	const lessThanSmall = useMediaQuery(muiTheme.breakpoints.down('sm'))
	const width = 100
	const height = lessThanSmall ? 37.5 : 18.75
	const radius = lessThanSmall ? 10 : 5
	const degree = 20

	// GSAP
	useGSAP(
		() => {
			gsap.timeline({
				scrollTrigger: {
					trigger: gridRef.current,
					start: 'top bottom',
					end: 'bottom top',
					// markers: true,
					onUpdate: self => {
						gsap.set(gridRef.current, {
							rotateY: lngDir === 'rtl' ? -2 * self.progress * degree + degree : 2 * self.progress * degree - degree
						})

						gsap.set('pattern', {
							attr: { x: `${self.progress * 100}%` }
						})
					}
				}
			})
		},
		{ scope: gridRef }
	)

	// Callbacks
	const generatePath = (padding: number) => {
		return (
			`M ${padding} ${radius}` +
			`Q ${padding} ${padding} ${radius} ${padding}` +
			`L ${width - radius} ${padding}` +
			`Q ${width - padding} ${padding} ${width - padding}  ${radius}` +
			`L ${width - padding} ${height - radius}` +
			`Q ${width - padding} ${height - padding} ${width - radius} ${height - padding}` +
			`L ${width / 2 + radius * 2} ${height - padding}` +
			`Q ${width / 2 + radius * 1.5} ${height - padding} ${width / 2 + radius} ${height + radius / 2 - padding}` +
			`Q ${width / 2} ${height + radius * 1.5 - padding} ${width / 2 - radius} ${height + radius / 2 - padding}` +
			`Q ${width / 2 - radius * 1.5} ${height - padding} ${width / 2 - radius * 2} ${height - padding}` +
			`L ${radius} ${height - padding}` +
			`Q ${padding} ${height - padding} ${padding} ${height - radius}` +
			`Z`
		)
	}

	return (
		<Grid ref={gridRef} container position="relative" sx={{ cursor: 'default' }}>
			<svg viewBox={`0 0 ${width} ${height + radius * 2}`} fill="none">
				<defs>
					<pattern id={patterId} patternUnits="userSpaceOnUse" width={lessThanSmall ? 9 : 4.5} height={lessThanSmall ? 7.5 : 3.75} patternTransform="rotate(45)">
						<line stroke="#ff8484" strokeWidth={lessThanSmall ? 7.5 : 3.75} x1={0} y={0} x2={0} y2={lessThanSmall ? 7.5 : 3.75} />
					</pattern>
				</defs>

				<path fill="#ff9191" d={generatePath(0)} />
				<path fill={`url(#${patterId})`} d={generatePath(lessThanSmall ? 2.5 : 1.25)} />

				<line
					stroke="#fff"
					strokeWidth={lessThanSmall ? 1.25 : 0.675}
					strokeLinecap="round"
					x1={width / 2}
					y1={height - radius / 2}
					x2={width / 2}
					y2={height + radius - radius / 2}
				/>

				<line
					stroke="#fff"
					strokeWidth={lessThanSmall ? 1.25 : 0.675}
					strokeLinecap="round"
					x1={width / 2 - radius / 2}
					y1={height}
					x2={width / 2}
					y2={height + radius - radius / 2}
				/>

				<line
					stroke="#fff"
					strokeWidth={lessThanSmall ? 1.25 : 0.675}
					strokeLinecap="round"
					x1={width / 2 + radius / 2}
					y1={height}
					x2={width / 2}
					y2={height + radius - radius / 2}
				/>
			</svg>

			<Typography
				component="h2"
				width={1}
				px={2}
				position="absolute"
				top="35%"
				left={0}
				fontSize={{ xs: 20, sm: 32, md: 40 }}
				fontWeight={700}
				letterSpacing={-1}
				textAlign="center"
				sx={{ transform: 'translateY(-50%)' }}
			>
				{t(text)}
			</Typography>
		</Grid>
	)
}

export default DialogueMolecule
